import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { errMsg } from "../../core/api";
import { useApp } from "../../core/AppContext";
import { fa, formatDate, monthRange, todayJ } from "../../core/format";
import MonthNav from "../../components/MonthNav";
import Select from "../../components/Select";
import ResponsiveList from "../../components/ResponsiveList";
import { TaskReportModal } from "./Tasks";

const STATUS_OPTS = [
  { value: "", label: "همه" },
  { value: "done", label: "انجام شده" },
  { value: "missed", label: "انجام نشده" },
];

const Pill = ({ done }) => (
  <span className={`badge ${done ? "ok" : "bad"}`}>
    <i
      className={`fa-solid ${done ? "fa-circle-check" : "fa-circle-xmark"}`}
      style={{ marginInlineEnd: 5 }}
    />
    {done ? "انجام شد" : "انجام نشد"}
  </span>
);

export default function AssignedTasks() {
  const { user, toast } = useApp();
  const manager = user.role !== "staff";
  const tj = todayJ();
  const [ym, setYm] = useState({ y: tj.jy, m: tj.jm });
  const [all, setAll] = useState(false);
  const [emps, setEmps] = useState([]);
  const [fEmp, setFEmp] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewTask, setViewTask] = useState(null);

  const range = useMemo(() => monthRange(ym.y, ym.m), [ym]);

  useEffect(() => {
    if (!manager) return;
    api
      .get("/employees")
      .then((r) => setEmps(r.data.items))
      .catch(() => {});
  }, [manager]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...(all ? {} : range),
        ...(fEmp ? { employeeId: fEmp } : {}),
        ...(fStatus ? { status: fStatus } : {}),
      };
      const { data } = await api.get("/daily-tasks/history", { params });
      setRows(data.items);
    } catch (e) {
      toast(errMsg(e), "err");
    } finally {
      setLoading(false);
    }
  }, [all, range, fEmp, fStatus, toast]);
  useEffect(() => {
    load();
  }, [load]);

  const doneCount = rows.filter((r) => r.done).length;
  const missedCount = rows.length - doneCount;
  const percent = rows.length ? Math.round((doneCount / rows.length) * 100) : 0;

  // خلاصه‌ی عملکرد هر نفر (برای مدیر بندر)
  const perEmp = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      if (!m.has(r.employeeId))
        m.set(r.employeeId, {
          id: r.employeeId,
          name: r.employeeName,
          done: 0,
          missed: 0,
        });
      m.get(r.employeeId)[r.done ? "done" : "missed"]++;
    });
    return [...m.values()].sort((a, b) => b.missed - a.missed);
  }, [rows]);

  const columns = [
    {
      key: "taskDate",
      label: "تاریخ وظیفه",
      render: (r) => formatDate(r.taskDate),
    },
    ...(manager
      ? [
          {
            key: "employeeName",
            label: "نام کارمند",
            render: (r) => <b>{r.employeeName}</b>,
          },
        ]
      : []),
    {
      key: "title",
      label: "وظیفه",
      render: (r) => (
        <div>
          <b>{r.title}</b>
          {r.description && <div className="hint">{r.description}</div>}
        </div>
      ),
    },
    { key: "status", label: "وضعیت", render: (r) => <Pill done={r.done} /> },
    {
      key: "doneAt",
      label: "زمان انجام",
      render: (r) =>
        r.done && r.doneAt ? formatDate(String(r.doneAt).slice(0, 10)) : "—",
    },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h2>وظایف محوله</h2>
          <p>
            {manager
              ? "وضعیت انجام وظایف گذشته‌ی پرسنل (سبز: انجام شده، قرمز: انجام نشده)"
              : "وظایف گذشته‌ی شما (سبز: انجام شده، قرمز: انجام نشده)"}
          </p>
        </div>
        {!all && <MonthNav ym={ym} onChange={setYm} />}
      </div>

      <div className="stats" style={{ marginBottom: 14 }}>
        <div className="card stat">
          <span>کل وظایف</span>
          <b>{fa(rows.length)}</b>
        </div>
        <div className="card stat good">
          <span>انجام شده</span>
          <b>{fa(doneCount)}</b>
        </div>
        <div className="card stat badx">
          <span>انجام نشده</span>
          <b>{fa(missedCount)}</b>
        </div>
        <div className="card stat">
          <span>درصد انجام</span>
          <b>{fa(percent)}٪</b>
        </div>
      </div>

      <div className="toolbar">
        {manager && (
          <div className="field">
            <label>کارمند</label>
            <Select
              value={fEmp}
              onChange={setFEmp}
              searchable
              options={[
                { value: "", label: "همه‌ی پرسنل" },
                ...emps.map((e) => ({
                  value: e.id,
                  label: `${e.firstName} ${e.lastName}`,
                })),
              ]}
            />
          </div>
        )}
        <div className="field">
          <label>وضعیت</label>
          <Select value={fStatus} onChange={setFStatus} options={STATUS_OPTS} />
        </div>
        <button
          className={`btn ${all ? "" : "btn-ghost"}`}
          onClick={() => setAll((v) => !v)}
          style={{ marginBottom: 14 }}
        >
          {all ? "نمایش بر اساس ماه" : "نمایش همه‌ی وظایف گذشته"}
        </button>
      </div>

      {manager && !fEmp && perEmp.length > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>کارمند</th>
                  <th>انجام شده</th>
                  <th>انجام نشده</th>
                  <th>درصد انجام</th>
                  <th>کل وظایف</th> 
                </tr>
              </thead>
              <tbody>
                {perEmp.map((p) => (
                  <tr
                    key={p.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => setFEmp(String(p.id))}
                    title="نمایش وظایف این فرد"
                  >
                    <td>
                      <b>{p.name}</b>
                    </td>
                    <td>
                      <span className="badge ok">{fa(p.done)}</span>
                    </td>
                    <td>
                      <span className="badge bad">{fa(p.missed)}</span>
                    </td>
                    <td>
                      {fa(Math.round((p.done / (p.done + p.missed)) * 100))}٪
                    </td>
                    <td>{fa(p.done + p.missed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <ResponsiveList
          loading={loading}
          rows={rows}
          empty="وظیفه‌ی گذشته‌ای یافت نشد"
          columns={columns}
          rowClass={(r) => (r.done ? "row-done" : "row-missed")}
          mobileTitle={(r) => r.title}
          mobileBadge={(r) => <Pill done={r.done} />}
          mobileMeta={(r) => [
            ...(manager
              ? [
                  {
                    icon: "fa-solid fa-user",
                    label: "کارمند",
                    value: r.employeeName,
                  },
                ]
              : []),
            {
              icon: "fa-regular fa-calendar",
              label: "تاریخ",
              value: formatDate(r.taskDate),
            },
            ...(r.done && r.doneAt
              ? [
                  {
                    icon: "fa-regular fa-circle-check",
                    label: "زمان انجام",
                    value: formatDate(String(r.doneAt).slice(0, 10)),
                  },
                ]
              : []),
          ]}
          actions={(r) =>
            r.done ? (
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setViewTask(r)}
              >
                <i className="fa-regular fa-file-lines" /> مشاهده شرح
              </button>
            ) : null
          }
        />
      </div>
{(() => {
  const listProps = {
    loading, empty: "وظیفه‌ی گذشته‌ای یافت نشد", columns,
    rowClass: (r) => (r.done ? "row-done" : "row-missed"),
    mobileTitle: (r) => r.title,
    mobileBadge: (r) => <Pill done={r.done} />,
    mobileMeta: (r) => [
      ...(manager ? [{ icon: "fa-solid fa-user", label: "کارمند", value: r.employeeName }] : []),
      { icon: "fa-regular fa-calendar", label: "تاریخ", value: formatDate(r.taskDate) },
    ],
    actions: (r) => r.done ? (
      <button className="btn btn-sm btn-ghost" onClick={() => setViewTask(r)}><i className="fa-regular fa-file-lines" /> مشاهده شرح</button>
    ) : null,
  };
  if (manager && !fEmp && !loading) {
    return perEmp.map((p) => {
      const total = p.done + p.missed;
      return (
        <div key={p.id} style={{ marginBottom: 18 }}>
          <div className="page-head" style={{ marginBottom: 8 }}>
            <h2 style={{ fontSize: 15 }}>{p.name}</h2>
            <div className="chips">
              <span className="chip">کل: {fa(total)}</span>
              <span className="badge ok">انجام‌شده: {fa(p.done)}</span>
              <span className="badge bad">انجام‌نشده: {fa(p.missed)}</span>
              <span className="chip">{fa(Math.round((p.done / total) * 100))}٪</span>
            </div>
          </div>
          <div className="card"><ResponsiveList {...listProps} rows={rows.filter((r) => r.employeeId === p.id)} /></div>
        </div>
      );
    });
  }
  return <div className="card"><ResponsiveList {...listProps} rows={rows} /></div>;
})()}
      {viewTask && (
        <TaskReportModal
          task={viewTask}
          mode="view"
          onClose={() => setViewTask(null)}
          onDone={() => setViewTask(null)}
        />
      )}
    </>
  );
}
